import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navbar, Breadcrumbs } from '../components';
import { Footer } from '../containers';
import './InterviewAssistantPage.css';

const SUBJECT_LABELS = {
  javascript: 'JavaScript',
  react: 'React',
  angular: 'Angular',
  aws: 'AWS',
};

const QUESTION_BANK = {
  javascript: [
    {
      id: 'js-1',
      question: 'Explain the difference between == and === in JavaScript.',
      keywords: ['coercion', 'type', 'strict', 'comparison', 'value'],
    },
    {
      id: 'js-2',
      question: 'What is event delegation and where is it useful?',
      keywords: ['bubbling', 'parent', 'listener', 'dynamic', 'events'],
    },
    {
      id: 'js-3',
      question: 'How does Promise.all differ from Promise.allSettled?',
      keywords: ['reject', 'fulfilled', 'status', 'parallel', 'promises'],
    },
  ],
  react: [
    {
      id: 'react-1',
      question: 'When would you use useMemo and useCallback in React?',
      keywords: ['rerender', 'memoization', 'dependencies', 'performance', 'reference'],
    },
    {
      id: 'react-2',
      question: 'Explain lifting state up and when context is a better fit.',
      keywords: ['shared state', 'parent', 'props', 'context', 'prop drilling'],
    },
    {
      id: 'react-3',
      question: 'How do error boundaries work and what are their limitations?',
      keywords: ['fallback', 'render', 'lifecycle', 'componentdidcatch', 'not async'],
    },
  ],
  angular: [
    {
      id: 'ng-1',
      question: 'What is dependency injection in Angular and why is it useful?',
      keywords: ['injector', 'service', 'provider', 'testability', 'singleton'],
    },
    {
      id: 'ng-2',
      question: 'Explain change detection strategies: Default vs OnPush.',
      keywords: ['zone', 'immutable', 'reference', 'performance', 'detection'],
    },
    {
      id: 'ng-3',
      question: 'How does RxJS help in Angular applications?',
      keywords: ['observable', 'subscription', 'operators', 'async', 'stream'],
    },
  ],
  aws: [
    {
      id: 'aws-1',
      question: 'When would you choose EC2 over Lambda?',
      keywords: ['serverless', 'runtime limits', 'control', 'cost', 'scaling'],
    },
    {
      id: 'aws-2',
      question: 'How do S3, CloudFront, and Route 53 work together for a web app?',
      keywords: ['cdn', 'dns', 'origin', 'static hosting', 'latency'],
    },
    {
      id: 'aws-3',
      question: 'What are IAM roles and least privilege in AWS?',
      keywords: ['permissions', 'policy', 'assume role', 'security', 'access'],
    },
  ],
};

const MAX_WARNINGS = 8;

const InterviewAssistantPage = () => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);
  const detectorRef = useRef(null);
  const monitorTimerRef = useRef(null);
  const warningCooldownRef = useRef({});
  const listeningIntentRef = useRef('answer');
  const transcriptRef = useRef('');
  const lastSpeechAtRef = useRef(0);
  const listeningStartedAtRef = useRef(0);
  const finalizeIntentRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const interviewStartedRef = useRef(false);

  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('javascript');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [speechError, setSpeechError] = useState('');
  const [botReply, setBotReply] = useState('Select subject, enable camera, and start interview.');
  const [warnings, setWarnings] = useState([]);
  const [faceMonitorAvailable, setFaceMonitorAvailable] = useState(false);

  const subjectQuestions = useMemo(() => QUESTION_BANK[selectedSubject] || [], [selectedSubject]);
  const currentQuestion = subjectQuestions[currentQuestionIndex] || subjectQuestions[0];

  const warningScore = useMemo(() => {
    const count = warnings.length;
    return Math.max(0, 100 - Math.min(100, Math.round((count / MAX_WARNINGS) * 100)));
  }, [warnings.length]);

  const pushWarning = (code, message) => {
    const now = Date.now();
    const last = warningCooldownRef.current[code] || 0;
    if (now - last < 4500) {
      return;
    }
    warningCooldownRef.current[code] = now;
    setWarnings((prev) => [{ id: `${code}-${now}`, message, ts: new Date().toLocaleTimeString() }, ...prev].slice(0, 20));
  };

  const speak = (text, onEnd) => {
    if (!window.speechSynthesis) {
      if (onEnd) {
        setTimeout(onEnd, 250);
      }
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.onend = () => {
      if (onEnd) {
        onEnd();
      }
    };
    window.speechSynthesis.speak(utterance);
  };

  const initSpeechRecognition = () => {
    const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      setSpeechError('Speech recognition is not supported in this browser.');
      return null;
    }
    const recognition = new SpeechRecognitionCtor();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let merged = '';
      for (let i = 0; i < event.results.length; i += 1) {
        merged += `${event.results[i][0].transcript} `;
      }
      transcriptRef.current = merged.trim();
      if (transcriptRef.current) {
        lastSpeechAtRef.current = Date.now();
      }
    };

    recognition.onerror = (event) => {
      setSpeechError(`Speech recognition error: ${event.error}`);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      const intent = finalizeIntentRef.current;
      finalizeIntentRef.current = null;
      if (!intent || !interviewStartedRef.current) {
        return;
      }
      const captured = transcriptRef.current.trim();
      if (intent === 'answer') {
        processAnswer(captured);
      } else {
        processReadiness(captured);
      }
    };

    return recognition;
  };

  const stopListeningAndFinalize = (intent) => {
    if (!recognitionRef.current || !isListening) {
      return;
    }
    finalizeIntentRef.current = intent;
    recognitionRef.current.stop();
  };

  const startAutoListening = (intent) => {
    if (!recognitionRef.current) {
      recognitionRef.current = initSpeechRecognition();
    }

    if (!recognitionRef.current) {
      return;
    }

    try {
      window.speechSynthesis?.cancel();
      listeningIntentRef.current = intent;
      transcriptRef.current = '';
      lastSpeechAtRef.current = Date.now();
      listeningStartedAtRef.current = Date.now();
      setSpeechError('');
      setIsListening(true);
      recognitionRef.current.start();
    } catch (err) {
      setIsListening(false);
      setSpeechError(`Unable to start speech recognition: ${err.message || 'unknown error'}`);
    }
  };

  const evaluateAnswer = (answer, keywords) => {
    const normalized = answer.toLowerCase();
    const matchedKeywords = keywords.filter((keyword) => normalized.includes(keyword.toLowerCase()));
    const keywordScore = Math.round((matchedKeywords.length / keywords.length) * 100);
    const lengthScore = Math.min(100, Math.round((answer.trim().split(/\s+/).filter(Boolean).length / 80) * 100));
    const totalScore = Math.round((keywordScore * 0.7) + (lengthScore * 0.3));

    let verdict = 'Needs improvement';
    let advice = 'Try to include definitions, one example, and edge cases.';

    if (totalScore >= 80) {
      verdict = 'Excellent answer';
      advice = 'Strong structure. Add one production trade-off to make it senior-level.';
    } else if (totalScore >= 60) {
      verdict = 'Good answer';
      advice = 'Cover more key terms and include one practical scenario.';
    }

    return {
      totalScore,
      matchedKeywords,
      verdict,
      advice,
    };
  };

  const processAnswer = (answerText) => {
    if (!answerText.trim()) {
      const text = 'I did not receive your answer clearly. Please speak again.';
      setBotReply(text);
      speak(text, () => {
        if (interviewStartedRef.current) {
          startAutoListening('answer');
        }
      });
      return;
    }

    const result = evaluateAnswer(answerText, currentQuestion.keywords);
    const reply = `${result.verdict}. Score ${result.totalScore}/100. Matched keywords: ${result.matchedKeywords.join(', ') || 'none'}. ${result.advice}`;
    const followUp = `${reply} Say yes if you are ready for the next question, or say no to continue this topic.`;

    setBotReply(followUp);
    speak(followUp, () => {
      if (interviewStartedRef.current) {
        startAutoListening('readiness');
      }
    });
  };

  const processReadiness = (answerText) => {
    const normalized = answerText.toLowerCase();
    const isYes = /\b(yes|yeah|yup|ready|sure|go ahead|next)\b/.test(normalized);
    const isNo = /\b(no|not yet|wait|hold|repeat)\b/.test(normalized);

    if (isYes) {
      const nextIndex = currentQuestionIndex + 1;
      if (nextIndex >= subjectQuestions.length) {
        const text = `Great work. You have completed the ${SUBJECT_LABELS[selectedSubject]} interview set.`;
        setBotReply(text);
        speak(text);
        stopInterview();
        return;
      }

      setCurrentQuestionIndex(nextIndex);
      const nextQuestionText = `Next question. ${subjectQuestions[nextIndex].question}`;
      setBotReply(nextQuestionText);
      speak(nextQuestionText, () => {
        if (interviewStartedRef.current) {
          startAutoListening('answer');
        }
      });
      return;
    }

    if (isNo) {
      const retry = `No problem. Answer this question again. ${currentQuestion.question}`;
      setBotReply(retry);
      speak(retry, () => {
        if (interviewStartedRef.current) {
          startAutoListening('answer');
        }
      });
      return;
    }

    const clarify = 'Please say yes for next question, or no to continue this question.';
    setBotReply(clarify);
    speak(clarify, () => {
      if (interviewStartedRef.current) {
        startAutoListening('readiness');
      }
    });
  };

  const startCamera = async () => {
    try {
      setCameraError('');
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);
    } catch (err) {
      setCameraError(`Camera access failed: ${err.message || 'permission denied'}`);
      setCameraOn(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOn(false);
  };

  const analyzeFaceFrame = async () => {
    if (!videoRef.current || !detectorRef.current || videoRef.current.readyState < 2) {
      return;
    }

    try {
      const faces = await detectorRef.current.detect(videoRef.current);

      if (!faces.length) {
        pushWarning('no-face', 'Warning: face not detected in camera frame.');
        return;
      }

      if (faces.length > 1) {
        pushWarning('multi-face', 'Warning: multiple faces detected.');
      }

      const face = faces[0];
      const frameWidth = videoRef.current.videoWidth || 1;
      const frameHeight = videoRef.current.videoHeight || 1;
      const faceCenterX = face.boundingBox.x + (face.boundingBox.width / 2);
      const faceCenterY = face.boundingBox.y + (face.boundingBox.height / 2);
      const offsetX = Math.abs((faceCenterX / frameWidth) - 0.5);
      const offsetY = Math.abs((faceCenterY / frameHeight) - 0.5);

      if (offsetX > 0.3 || offsetY > 0.32) {
        pushWarning('look-away', 'Warning: possible look-away detected. Keep eyes near screen center.');
      }
    } catch {
      // Silent fallback for detector runtime issues.
    }
  };

  const startMonitoring = () => {
    if (monitorTimerRef.current) {
      clearInterval(monitorTimerRef.current);
    }

    monitorTimerRef.current = setInterval(() => {
      analyzeFaceFrame();
    }, 1400);
  };

  const stopMonitoring = () => {
    if (monitorTimerRef.current) {
      clearInterval(monitorTimerRef.current);
      monitorTimerRef.current = null;
    }
  };

  const startInterview = () => {
    if (!cameraOn) {
      const text = 'Please enable camera first before starting the interview.';
      setBotReply(text);
      speak(text);
      return;
    }

    if (!subjectQuestions.length) {
      const text = 'No questions found for this subject.';
      setBotReply(text);
      speak(text);
      return;
    }

    setInterviewStarted(true);
    interviewStartedRef.current = true;
    setWarnings([]);
    setCurrentQuestionIndex(0);
    const text = `Interview started for ${SUBJECT_LABELS[selectedSubject]}. Question 1. ${subjectQuestions[0].question}`;
    setBotReply(text);
    speak(text, () => {
      if (interviewStartedRef.current) {
        startAutoListening('answer');
      }
    });
    startMonitoring();
  };

  const stopInterview = () => {
    setInterviewStarted(false);
    interviewStartedRef.current = false;
    stopMonitoring();
    if (recognitionRef.current && isListening) {
      finalizeIntentRef.current = null;
      recognitionRef.current.stop();
    }
    setIsListening(false);
    window.speechSynthesis?.cancel();
  };

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && interviewStarted) {
        pushWarning('tab-hidden', 'Warning: tab switch detected. Stay on the interview screen.');
      }
    };

    const handleBlur = () => {
      if (interviewStarted) {
        pushWarning('window-blur', 'Warning: window focus lost.');
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('blur', handleBlur);
    };
  }, [interviewStarted]);

  useEffect(() => {
    if ('FaceDetector' in window) {
      try {
        detectorRef.current = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 2 });
        setFaceMonitorAvailable(true);
      } catch {
        setFaceMonitorAvailable(false);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      stopInterview();
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    interviewStartedRef.current = interviewStarted;
  }, [interviewStarted]);

  useEffect(() => {
    if (autoStopTimerRef.current) {
      clearInterval(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }

    if (!isListening || !interviewStarted) {
      return undefined;
    }

    autoStopTimerRef.current = setInterval(() => {
      const now = Date.now();
      const inactiveMs = now - lastSpeechAtRef.current;
      const elapsedMs = now - listeningStartedAtRef.current;
      const words = transcriptRef.current.split(/\s+/).filter(Boolean).length;
      if (words >= 2 && inactiveMs > 1700) {
        stopListeningAndFinalize(listeningIntentRef.current);
        return;
      }
      if (elapsedMs > 45000) {
        stopListeningAndFinalize(listeningIntentRef.current);
      }
    }, 300);

    return () => {
      if (autoStopTimerRef.current) {
        clearInterval(autoStopTimerRef.current);
        autoStopTimerRef.current = null;
      }
    };
  }, [interviewStarted, isListening]);

  const breadcrumbItems = [
    { label: 'Home', to: '/' },
    { label: 'Interview Assistant' },
  ];

  return (
    <div className="ia-page">
      <div className="gradient__bg">
        <Navbar />
      </div>
      <Breadcrumbs items={breadcrumbItems} />

      <section className="ia-shell">
        <header className="ia-header">
          <div>
            <h1>Virtual Interview Assistant (Free MVP)</h1>
            <p>
              Browser-only interviewer with automatic voice flow. It asks questions,
              listens without clicks, auto-evaluates answers, then asks if you are ready for the next question.
            </p>
          </div>
          <div className="ia-score">
            <span>Integrity Score</span>
            <strong>{warningScore}%</strong>
          </div>
        </header>

        <section className="ia-actions">
          <label className="ia-subject-field" htmlFor="ia-subject">
            Subject
            <select
              id="ia-subject"
              value={selectedSubject}
              onChange={(event) => {
                setSelectedSubject(event.target.value);
                setCurrentQuestionIndex(0);
              }}
              disabled={interviewStarted}
            >
              {Object.entries(SUBJECT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <button type="button" onClick={startCamera} disabled={cameraOn}>Enable Camera</button>
          <button type="button" onClick={stopCamera} disabled={!cameraOn}>Disable Camera</button>
          <button type="button" onClick={startInterview} disabled={!cameraOn || interviewStarted}>Start Interview</button>
          <button type="button" onClick={stopInterview} disabled={!interviewStarted}>Stop Interview</button>
        </section>

        <section className="ia-stage">
          <article className="ia-panel">
            <h2>Your Camera</h2>
            <div className="ia-video-wrap">
              <video ref={videoRef} autoPlay muted playsInline />
              {!cameraOn ? <div className="ia-video-overlay">Camera preview will appear here.</div> : null}
            </div>
            {cameraError ? <p className="ia-error">{cameraError}</p> : null}
            {!faceMonitorAvailable ? (
              <p className="ia-note">Face monitoring uses FaceDetector API and may be unavailable in some browsers.</p>
            ) : null}
          </article>

          <article className="ia-panel">
            <h2>Humanoid Interviewer</h2>
            <div className="ia-robot-wrap">
              <div className="ia-robot-head">
                <div className="ia-robot-eye" />
                <div className="ia-robot-eye" />
              </div>
              <div className="ia-robot-mouth" />
            </div>
            <div className="ia-bot-text">
              <strong>Current question:</strong>
              <p>{currentQuestion?.question || 'Select subject and start interview.'}</p>
              <strong>Interviewer feedback:</strong>
              <p>{botReply}</p>
              <strong>Listening status:</strong>
              <p>{isListening ? 'Listening to candidate...' : interviewStarted ? 'Waiting or speaking...' : 'Idle'}</p>
            </div>
          </article>
        </section>

        {speechError ? <p className="ia-error ia-inline-error">{speechError}</p> : null}

        <section className="ia-warning-zone">
          <h3>Warnings</h3>
          {!warnings.length ? (
            <p className="ia-note">No warnings yet. Keep focus on this tab and stay centered in camera frame.</p>
          ) : (
            <ul>
              {warnings.map((warning) => (
                <li key={warning.id}>
                  <span>{warning.ts}</span>
                  <span>{warning.message}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </section>

      <Footer />
    </div>
  );
};

export default InterviewAssistantPage;
